// FECOA-1270 — encrypted IndexedDB persistence for the participant offline
// queue. Queued survey answers are clinical data (PII at rest), so payloads
// are AES-GCM-256 encrypted under a non-extractable Web Crypto key stored in
// the same database. Non-extractable means no script — including an XSS
// payload — can ever read the raw key bytes, and a copied browser profile
// yields ciphertext without a usable key. The only plaintext columns are the
// opaque ids the queue needs to operate without decrypting: participantId
// (scopes flushing to the authenticated participant), assignmentId (request
// grouping), kind, and queuedAt. Storage decision write-up:
// flexcoa-docs/features/participants/web-offline-queue.md

const DB_NAME = 'flexcoa-offline-queue';
const DB_VERSION = 1;
const QUEUE_STORE = 'queue';
const KEY_STORE = 'crypto';
const DEVICE_KEY_ID = 'device-key';

let dbPromise = null;

const openDb = () => {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(QUEUE_STORE)) {
        const store = db.createObjectStore(QUEUE_STORE, { keyPath: 'id', autoIncrement: true });
        store.createIndex('participantId', 'participantId', { unique: false });
      }
      if (!db.objectStoreNames.contains(KEY_STORE)) {
        db.createObjectStore(KEY_STORE, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => {
      dbPromise = null;
      reject(request.error || new Error('IndexedDB unavailable'));
    };
    request.onblocked = () => {
      dbPromise = null;
      reject(new Error('IndexedDB open blocked'));
    };
  });
  return dbPromise;
};

const tx = (db, store, mode, fn) =>
  new Promise((resolve, reject) => {
    const transaction = db.transaction(store, mode);
    const objectStore = transaction.objectStore(store);
    const result = fn(objectStore);
    transaction.oncomplete = () => resolve(result?.result ?? result);
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error || new Error('Transaction aborted'));
  });

const getOrCreateKey = async (db) => {
  const existing = await tx(db, KEY_STORE, 'readonly', (s) => s.get(DEVICE_KEY_ID));
  if (existing?.key) return existing.key;
  const key = await crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    false, // non-extractable: key bytes can never be read out by script
    ['encrypt', 'decrypt']
  );
  await tx(db, KEY_STORE, 'readwrite', (s) => s.put({ id: DEVICE_KEY_ID, key }));
  return key;
};

const encryptPayload = async (db, payload) => {
  const key = await getOrCreateKey(db);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(JSON.stringify(payload));
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encoded);
  return { iv, ciphertext };
};

const decryptPayload = async (db, row) => {
  const key = await getOrCreateKey(db);
  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: row.iv },
    key,
    row.ciphertext
  );
  return JSON.parse(new TextDecoder().decode(plaintext));
};

export const addRow = async ({ participantId, assignmentId, kind, payload }) => {
  const db = await openDb();
  const { iv, ciphertext } = await encryptPayload(db, payload);
  const row = {
    participantId: String(participantId),
    assignmentId: String(assignmentId),
    kind,
    queuedAt: Date.now(),
    iv,
    ciphertext,
  };
  return tx(db, QUEUE_STORE, 'readwrite', (s) => s.add(row));
};

// Decrypted rows for one participant: [{ id, assignmentId, kind, queuedAt, payload }].
// A row that fails to decrypt (corrupted, or written under a lost key) is
// dropped from the result and deleted — it can never be synced.
export const getRows = async (participantId) => {
  const db = await openDb();
  const rows = await new Promise((resolve, reject) => {
    const request = db
      .transaction(QUEUE_STORE, 'readonly')
      .objectStore(QUEUE_STORE)
      .index('participantId')
      .getAll(String(participantId));
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
  const decrypted = [];
  const unreadableIds = [];
  for (const row of rows) {
    try {
      const payload = await decryptPayload(db, row);
      decrypted.push({
        id: row.id,
        assignmentId: row.assignmentId,
        kind: row.kind,
        queuedAt: row.queuedAt,
        payload,
      });
    } catch {
      unreadableIds.push(row.id);
    }
  }
  if (unreadableIds.length) await deleteRows(unreadableIds);
  return decrypted;
};

export const countRows = async (participantId) => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const request = db
      .transaction(QUEUE_STORE, 'readonly')
      .objectStore(QUEUE_STORE)
      .index('participantId')
      .count(String(participantId));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

export const deleteRows = async (ids) => {
  if (!ids.length) return;
  const db = await openDb();
  await tx(db, QUEUE_STORE, 'readwrite', (s) => {
    for (const id of ids) s.delete(id);
  });
};

// Wholesale purge of one participant's rows — only used when the server
// reports the participant deactivated (it will never accept that data).
export const clearParticipant = async (participantId) => {
  const db = await openDb();
  const rows = await new Promise((resolve, reject) => {
    const request = db
      .transaction(QUEUE_STORE, 'readonly')
      .objectStore(QUEUE_STORE)
      .index('participantId')
      .getAllKeys(String(participantId));
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
  await deleteRows(rows);
};
